import { observer } from 'mobx-react';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { GButton } from '@monorepo/base/src/components/buttons/group/button-group';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { sentenceCase } from 'change-case';
import { IAdminxStore } from '../../../../stores';
import { Statuses } from '../../../../enums/status';
import { CampaignTargetingLinkChoicePolicy } from '../../../../models/bidder-campaign/bidder-campaign.types';
import {
	BidderCampaignTargetingLinkCidModel,
	BidderCampaignTargetingLinkModel,
} from '../../../../models/bidder-campaign/bidder-campaign-targeting-link.model';
import { BidderCampaignTargetingModel } from '../../../../models/bidder-campaign/bidder-campaign-targeting.model';
import { BidderApi } from '../../../../apis/bidder.api';
import { TargetingLinkCid } from './bidder-targeting-links';
import styles from './bidder-targeting-links-table.module.scss';
import React from 'react';
import { Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';

type Props = {
	campaignId: id | undefined;
	targeting: BidderCampaignTargetingModel;
	links: BidderCampaignTargetingLinkModel[];
	policy: CampaignTargetingLinkChoicePolicy;
};

const EditableNumberCell = observer(
	({
		value,
		onCommit,
		debugId,
		className,
	}: {
		value: number | null | undefined;
		onCommit: (next: number) => void;
		debugId: string;
		className?: string;
	}) => {
		const displayValue = value == null || Number.isNaN(value) ? '' : String(value);

		return (
			<Input
				className={className || styles.fieldInput}
				value={displayValue}
				onValue={next => {
					if (next === '') {
						onCommit(0);
						return;
					}
					if (!Number.isNaN(Number(next))) {
						onCommit(Number(next));
					}
				}}
				debugProps={{ dataAttrs: [new DataAttribute('id', debugId)] }}
			/>
		);
	}
);

const CidsCell = observer(({ targetingLink }: { targetingLink: BidderCampaignTargetingLinkModel }) => {
	return (
		<div className={styles.cidsCell}>
			<div className={styles.cidsList}>
				{targetingLink.cids?.map((cid: BidderCampaignTargetingLinkCidModel, cidIndex) => (
					<TargetingLinkCid key={`${cid.id ?? 'new'}-${cidIndex}`} cid={cid} />
				))}
			</div>
			<Icon size={'1.25rem'} className={styles.addCidButton} isMFP onClick={targetingLink.addNewCid}>
				plus
			</Icon>
		</div>
	);
});

export const TargetingLinksTable = observer(({ campaignId, targeting, links, policy }: Props) => {
	const { bidderStore } = useStores<IAdminxStore>();
	const campaignCrudStore = bidderStore.getCrud();
	const activePublishers = campaignCrudStore.getData().campaign.campaign_publisher_lists?.white_list;
	const { currentRoute } = useRoute();
	const isEditRoute = /\/edit\/?$/.test(currentRoute);
	const showPublisher = policy === CampaignTargetingLinkChoicePolicy.PerPublisher;
	const showWeight = policy === CampaignTargetingLinkChoicePolicy.ByWeight;

	const deleteTargetingLinkFromCache = async (yid: string | undefined) => {
		if (!campaignId || !yid) {
			return;
		}
		try {
			await BidderApi.deleteTargetingLinkFromCache({ campaign_id: campaignId, yids: [yid] });
			await campaignCrudStore.getBidderCampaign(campaignId);
		} catch (error) {
			console.error('Error deleting targeting link from cache', error);
		}
	};

	if (!links.length) {
		return null;
	}

	return (
		<div className={styles.tableWrapper}>
			<table className={styles.linksTable}>
				<thead>
					<tr>
						<th>Status</th>
						<th>YID</th>
						{showPublisher ? <th>Publisher</th> : null}
						<th>Paused in cache</th>
						<th>Encode URL</th>
						{showWeight ? <th>Weight</th> : <th>Max conv.</th>}
						<th>RH offer id</th>
						<th>RH publisher id</th>
						<th>Black hour</th>
						<th>Max loss</th>
						<th>Max clicks</th>
						<th>CIDs</th>
						<th>Dest link</th>
					</tr>
				</thead>
				<tbody>
					{links.map((targetingLink, index) => {
						const rowKey = `${targetingLink.id ?? 'new'}-${index}`;
						return (
							<tr key={rowKey}>
								<td>
									<Toggle
										label={
											targetingLink.status === Statuses.Active
												? sentenceCase(Statuses.Active)
												: sentenceCase(Statuses.Paused)
										}
										onChange={status => {
											targetingLink.status = status ? Statuses.Active : Statuses.Paused;
										}}
										defaultValue={targetingLink.status === Statuses.Active}
									/>
								</td>
								<td>
									<Input
										className={styles.yidInput}
										value={targetingLink.yid}
										onValue={value => {
											targetingLink.yid = value;
										}}
									/>
								</td>
								{showPublisher ? (
									<td className={styles.publisherDropdown}>
										<Dropdown
											required={true}
											defaultOption={
												activePublishers?.find(p => p.pid === targetingLink.pid?.toString())?.publisher_name
											}
											options={activePublishers?.map(i => i.publisher_name) || []}
											onSelect={selectedPublisher => {
												const publisherName = selectedPublisher?.values().next().value;
												targetingLink.pid = activePublishers?.find(p => p.publisher_name === publisherName)?.pid;
											}}
										/>
									</td>
								) : null}
								<td>
									<div className={styles.cacheCell}>
										{targetingLink.paused_in_cache === 1 ? (
											<React.Fragment>
												{isEditRoute ? (
													<GButton
														className={styles.cleanFromCacheButton}
														text="Clean From Cache"
														onClick={() => {
															deleteTargetingLinkFromCache(targetingLink.yid);
														}}
													/>
												) : null}
												<span className={styles.cacheReasonIcon}>
													<Tooltip content={targetingLink.paused_reason || '—'} isFullWidth={false}>
														<Icon isMFP size="0.9rem">
															info-circle
														</Icon>
													</Tooltip>
												</span>
											</React.Fragment>
										) : null}
									</div>
								</td>
								<td>
									<Toggle
										onChange={encodeUrl => {
											targetingLink.encode_url = encodeUrl;
										}}
										defaultValue={targetingLink.encode_url}
									/>
								</td>
								<td>
									{showWeight ? (
										<EditableNumberCell
											value={targetingLink.weight}
											onCommit={next => {
												targetingLink.weight = next;
											}}
											debugId={`weight_${rowKey}`}
										/>
									) : (
										<EditableNumberCell
											className={styles.fiveDigitsInput}
											value={targetingLink.max_conversions}
											onCommit={next => {
												targetingLink.max_conversions = next;
											}}
											debugId={`max_conversions_${rowKey}`}
										/>
									)}
								</td>
								<td>
									<Input
										className={styles.fieldInput}
										value={targetingLink.rh_oid}
										onValue={v => {
											targetingLink.rh_oid = v;
											if (!targeting.id) {
												targeting.rh_offer_id = v;
											}
										}}
									/>
								</td>
								<td>
									<Input
										className={styles.fieldInput}
										value={targetingLink.rh_pid}
										onValue={v => {
											targetingLink.rh_pid = v;
											if (!targeting.id) {
												targeting.rh_publisher_id = v;
											}
										}}
									/>
								</td>
								<td>
									<Input
										className={styles.blackHourInput}
										value={targetingLink.black_hour}
										onValue={value => {
											targetingLink.black_hour = value;
										}}
										debugProps={{ dataAttrs: [new DataAttribute('id', `black_hour_${rowKey}`)] }}
									/>
								</td>
								<td>
									<EditableNumberCell
										className={styles.fiveDigitsInput}
										value={targetingLink.max_loss_per_link}
										onCommit={next => {
											targetingLink.max_loss_per_link = next;
										}}
										debugId={`max_loss_${rowKey}`}
									/>
								</td>
								<td>
									<EditableNumberCell
										className={styles.fiveDigitsInput}
										value={targetingLink.max_clicks_per_link}
										onCommit={next => {
											targetingLink.max_clicks_per_link = next;
										}}
										debugId={`max_clicks_${rowKey}`}
									/>
								</td>
								<td>
									<CidsCell targetingLink={targetingLink} />
								</td>
								<td>
									<Input
										className={styles.destLinkInput}
										value={targetingLink.dest_link}
										onValue={value => {
											targetingLink.dest_link = value;
										}}
									/>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div className={styles.blackHourHint}>Black hour: each hour should be separated by a comma (“,”)</div>
		</div>
	);
});
